import AnimatedSection from '../../components/base/AnimatedSection';
import AnimatedImage from '../../components/base/AnimatedImage';

export default function AboutPage() {
  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <AnimatedSection className="py-20 bg-gradient-to-br from-green-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center stagger-children">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">About 918Turf</h1>
          <p className="text-xl text-gray-600 leading-relaxed">Tulsa's premier synthetic turf distributor, committed to quality, service, and local expertise</p>
        </div>
      </AnimatedSection>

      {/* Our Story */}
      <AnimatedSection className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="stagger-children">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <p className="text-lg text-gray-600 mb-4 leading-relaxed">918Turf was founded with a simple mission: to provide Tulsa and surrounding communities with access to premium-quality synthetic turf at wholesale prices, backed by exceptional local service.</p>
              <p className="text-lg text-gray-600 mb-4 leading-relaxed">As Oklahoma natives, we understand the unique challenges of maintaining natural grass in our climate—from scorching summers to unpredictable weather patterns. We saw an opportunity to help homeowners, businesses, and sports facilities achieve beautiful, durable outdoor spaces without the constant maintenance, water waste, and expense of traditional lawns.</p>
              <p className="text-lg text-gray-600 leading-relaxed">Today, 918Turf is the trusted choice for contractors, landscapers, facility managers, and homeowners throughout the Tulsa metro area who demand the best in synthetic turf products and service.</p>
            </div>
            <AnimatedImage 
              src="https://public.readdy.ai/ai/img_res/c7e688a361f24b0b9d232296c78e4613.jpg" 
              alt="918Turf Distribution Center Tulsa" 
              className="rounded-2xl shadow-2xl h-96 w-full"
            />
          </div>
        </div>
      </AnimatedSection>

      {/* Why Choose Us */}
      <AnimatedSection className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20 stagger-children">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Why Tulsa Chooses 918Turf</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">We're more than just a turf supplier—we're your partner in creating beautiful, sustainable outdoor spaces.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            {[
              {
                icon: 'ri-flag-line',
                title: 'Made In USA Quality',
                description: 'Every roll of turf we sell is manufactured right here in the United States. This means superior quality control, consistent product standards, and support for American jobs. Our turf meets the highest industry standards for durability, UV resistance, and realistic appearance.'
              },
              {
                icon: 'ri-price-tag-3-line',
                title: 'Wholesale Pricing',
                description: 'We offer true wholesale pricing open to the public. No membership fees, no minimum orders for homeowners. Contractors and landscapers receive additional volume discounts. We believe premium synthetic turf should be accessible to everyone in Tulsa.'
              },
              {
                icon: 'ri-map-pin-line',
                title: 'Local Tulsa Service',
                description: 'We\'re based right here in Tulsa and serve the entire metro area. That means fast delivery, local pickup options, and personalized service from people who understand Oklahoma\'s climate and your specific needs. We\'re your neighbors, not a distant warehouse.'
              },
              {
                icon: 'ri-shield-check-line',
                title: '10 Year Warranty',
                description: 'We stand behind our products with an industry-leading 10-year warranty. This covers UV stability, color retention, and material integrity. If there\'s ever an issue with your turf, we\'ll make it right. That\'s our commitment to you.'
              },
              {
                icon: 'ri-tools-line',
                title: 'Complete Installation Support',
                description: 'We provide everything you need for a successful installation: detailed instructions, expert advice, and all the supplies including adhesives, seaming tape, infill, and tools. DIY or professional installation—we support both approaches.'
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Expert Guidance',
                description: 'Our team has extensive experience with synthetic turf installations across all applications. We\'ll help you choose the right product for your specific needs, calculate quantities accurately, and provide ongoing support throughout your project.'
              }
            ].map((feature, index) => (
              <div 
                key={feature.title}
                className="bg-white p-10 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300"
                style={{ 
                  opacity: 0,
                  animation: 'fadeIn 400ms ease-out forwards',
                  animationDelay: `${index * 100}ms`,
                  transform: 'translateY(0)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div className="w-16 h-16 flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-5xl text-green-700`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Our Commitment */}
      <AnimatedSection className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <AnimatedImage
              src="https://readdy.ai/api/search-image?query=beautiful%20residential%20home%20in%20Tulsa%20Oklahoma%20with%20pristine%20artificial%20synthetic%20turf%20lawn%2C%20modern%20house%20exterior%2C%20lush%20green%20fake%20grass%20yard%2C%20professional%20landscaping%2C%20clean%20simple%20background%20showing%20quality%20turf%20installation&width=800&height=900&seq=commitment918&orientation=portrait" 
              alt="Quality Turf Installation Tulsa"
              className="rounded-2xl shadow-2xl w-full h-auto"
            />
            <div className="stagger-children">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">Our Commitment To Tulsa</h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  At 918Turf, we're committed to being more than just a supplier. We're invested in the Tulsa community and dedicated to helping our neighbors create beautiful, sustainable outdoor spaces that enhance property values and quality of life.
                </p>
                <p>
                  <strong className="text-gray-900">Environmental Responsibility:</strong> Synthetic turf helps conserve thousands of gallons of water annually—critical in Oklahoma's climate. It eliminates the need for harmful pesticides, fertilizers, and gas-powered lawn equipment, reducing your carbon footprint.
                </p>
                <p>
                  <strong className="text-gray-900">Customer Education:</strong> We believe informed customers make better decisions. That's why we provide comprehensive resources, honest advice, and transparent pricing. No pressure, no gimmicks—just straightforward information to help you choose wisely.
                </p>
                <p>
                  <strong className="text-gray-900">Long-Term Support:</strong> Our relationship doesn't end at the sale. We're here to answer questions, provide maintenance tips, and support you throughout the life of your turf. Your success is our success.
                </p>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Service Area */}
      <AnimatedSection className="py-24 bg-gradient-to-br from-green-700 to-green-900">
        <div className="max-w-7xl mx-auto px-6 text-center stagger-children">
          <h2 className="text-5xl font-bold text-white mb-6">Proudly Serving The Tulsa Metro Area</h2>
          <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
            We deliver premium synthetic turf and installation supplies throughout Tulsa County and surrounding communities.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-12">
            {['Tulsa', 'Broken Arrow', 'Bixby', 'Owasso', 'Jenks', 'Sapulpa', 'Sand Springs', 'Glenpool', 'Claremore', 'Catoosa', 'Collinsville', 'Skiatook'].map((city, index) => (
              <div 
                key={city}
                className="text-white text-lg font-medium"
                style={{ 
                  opacity: 0,
                  animation: 'fadeIn 400ms ease-out forwards',
                  animationDelay: `${index * 60}ms`
                }}
              >
                {city}
              </div>
            ))}
          </div>
          <a 
            href="tel:918-806-8873" 
            className="inline-block bg-white text-green-700 px-10 py-5 rounded-full text-lg font-bold hover:bg-gray-100 hover:shadow-xl transition-all duration-200 whitespace-nowrap"
            style={{ transform: 'translateY(0)' }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            Call 918-806-8873 Today
          </a>
        </div>
      </AnimatedSection>
    </div>
  );
}
